import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { History } from 'history';

import routes from '@/config/routes';
import { useAppDispatch } from '@/hooks';
import { getProfile } from '@/store/slices/authSlice';
import { ApplicationStatus, AttendanceType, FraudGroupType, GigApplyType, StageTwoType } from '@/store/enums';
import { createGigNavigationHistory } from '@/store/slices/gigSlice';
import type { IGigApply, INavigationHistory, IStageTwoContext, IUser, ApplicationContextType } from '@/store/types';
import { createNavigationHistory } from '@/helpers/gigHelper';
import { FULFILLED_ACTION } from '@/config/variables';
import { applicationApis } from 'src/api/sp/application';
import { SpeedTestSessionKey } from '@/constants';

export const PostAuthRedirect = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  useEffect(() => {
    dispatch(getProfile()).then(response => {
      localStorage.setItem('isLoggedIn', 'true');
      if (response.meta.requestStatus !== FULFILLED_ACTION) {
        history.push(routes.root);
        return;
      }
      const gigApply = getGigApply();
      if (gigApply && gigApply.gigId !== '') {
        redirectToPersistedGig(gigApply);
        return;
      }

      if (localStorage.getItem(AttendanceType.AttendanceSessionKey)) {
        redirectToAttendanceTracker();
        return;
      }

      if (localStorage.getItem(SpeedTestSessionKey)) {
        redirectToSpeedTest();
        return;
      }

      if (localStorage.getItem(FraudGroupType.FraudGroupKey)) {
        redirectToFraudTab();
        return;
      }

      const stageTwoContext = getStageTwoContext();
      if (isIUser(response?.payload)) {
        stageTwoContext.userId = response?.payload?.id;
      }
      if (stageTwoContext && stageTwoContext.applicationId !== '') {
        console.log('Redirecting to Stage 2');
        redirectToStageTwo(stageTwoContext, history);
        return;
      }

      history.push(routes.root);
    });
  }, []);

  const getGigApply = (): IGigApply => {
    let gigApplyResult = { gigId: '', originUrl: '', adSourceId: null, refId: null, countryId: 0 } as IGigApply;
    const localStorageKey = `${GigApplyType.GigApplyKey}`;

    if (localStorage[localStorageKey]) {
      const result = JSON.parse(localStorage.getItem(localStorageKey) || '');
      gigApplyResult = { ...result, countryId: result.countryId || 0 };
    }

    return gigApplyResult;
  };

  const redirectToAttendanceTracker = () => {
    const payload = JSON.parse(localStorage.getItem(AttendanceType.AttendanceSessionKey) || '');
    const { timetableID, timetableIdHash } = payload;
    localStorage.removeItem(AttendanceType.AttendanceSessionKey);
    history.push(`${routes.auth.markAttendance}/${timetableID}/${timetableIdHash}`);
  };

  const redirectToFraudTab = () => {
    const payload = JSON.parse(localStorage.getItem(FraudGroupType.FraudGroupKey) || '');
    const { userId, selectedTab } = payload;
    localStorage.removeItem(FraudGroupType.FraudGroupKey);
    history.push(`${routes.admin.agents.serviceProviders}?userId=${userId}&selectedTab=${selectedTab}`);
  };

  const redirectToSpeedTest = () => {
    localStorage.removeItem(SpeedTestSessionKey);
    history.push(`${routes.auth.speedTest}`);
  };

  const redirectToPersistedGig = (gigApply: IGigApply): void => {
    const redirectTo = routes.sp.onboarding.gigs + `?gig_id=${gigApply.gigId}`;
    localStorage.removeItem(GigApplyType.GigApplyKey);
    const payload: INavigationHistory = createNavigationHistory(
      gigApply.originUrl,
      +gigApply.gigId,
      gigApply.adSourceId,
      gigApply.refId,
      gigApply.countryId,
      gigApply.urlString,
    );
    dispatch(createGigNavigationHistory(payload));
    console.log('redirect to persisted gig page');
    history.push(redirectTo);
  };

  return <></>;
};

export const getStageTwoContext = (): IStageTwoContext => {
  let stageTwoResult = { applicationId: '', originUrl: '' };
  const localStorageKey = `${StageTwoType.StageTwoKey}`;

  if (localStorage[localStorageKey]) {
    const result = JSON.parse(localStorage.getItem(localStorageKey) || '');
    stageTwoResult = { ...result };
  }

  return stageTwoResult;
};

export const redirectToStageTwo = (stageTwoContext: IStageTwoContext, history: History<unknown>): void => {
  getApplicationContext(stageTwoContext.applicationId).then(context => {
    const { clientId, gigId, userId, applicationStatusId } = context;
    const redirectUrl = routes.sp.expiredLink;

    if (stageTwoContext.userId !== userId || applicationStatusId === ApplicationStatus.DeclinedApplication) {
      history.push(redirectUrl);
      return;
    }

    const redirectTo = `/resume-builder/${clientId}/${gigId}/submitted`;

    console.log('Redirecting to persisted Stage Two');
    history.push(redirectTo);
  });
};

const getApplicationContext = async (applicationId: string): Promise<ApplicationContextType> => {
  const response = await applicationApis.getApplicationContext(applicationId);
  return response.data;
};

const isIUser = (obj: any): obj is IUser => 'id' in obj;
